/* ===========================================================
   music/radio.js — Weather Radio: endless music made from the
   weather outside, never the same twice

   The weather sets the mood:
     sky   → the scale (sunny = major, cloudy = dorian, rain = minor,
             storms = dark phrygian, snow = soft pentatonic, night = lydian)
     wind  → tempo and how busy the drums are
     temp  → how bright the sound is and how high the key sits
     rain  → a rain layer under the music; storms add thunder
   =========================================================== */

import { tone, kick, snare, hat, rainLayer, thunder, clock, midi } from './synth.js';

const SCALES = {
  major:      [0, 2, 4, 5, 7, 9, 11],
  lydian:     [0, 2, 4, 6, 7, 9, 11],
  dorian:     [0, 2, 3, 5, 7, 9, 10],
  minor:      [0, 2, 3, 5, 7, 8, 10],
  phrygian:   [0, 1, 3, 5, 7, 8, 10],
  pentatonic: [0, 2, 4, 7, 9, 12, 14],
};
const PROGS = {
  major: [[0, 5, 3, 4], [0, 3, 5, 4], [0, 4, 5, 3]],
  lydian: [[0, 1, 0, 4], [0, 2, 1, 0]],
  dorian: [[0, 3, 0, 6], [0, 6, 3, 4]],
  minor: [[0, 5, 2, 6], [0, 3, 4, 0], [0, 5, 3, 4]],
  phrygian: [[0, 1, 0, 6], [0, 1, 3, 1]],
  pentatonic: [[0, 3, 1, 4], [0, 4, 3, 1]],
};
const NAMES = ['C', 'C#', 'D', 'Eb', 'E', 'F', 'F#', 'G', 'Ab', 'A', 'Bb', 'B'];

/** What the weather sounds like. wx: { code, isDay, temp (°F), wind (mph), rain (0-100 %) } */
export function moodOf(wx) {
  const c = wx.code ?? 0;
  const storm = c >= 95, snow = (c >= 71 && c <= 77) || c === 85 || c === 86;
  const wet = (c >= 51 && c <= 67) || (c >= 80 && c <= 82);
  const cloudy = c === 3 || (c >= 45 && c <= 48);
  const scale = storm ? 'phrygian' : snow ? 'pentatonic' : wet ? 'minor' : cloudy ? 'dorian' : wx.isDay === false ? 'lydian' : 'major';
  const t = wx.temp ?? 60, w = wx.wind ?? 5;
  const bpm = Math.round(Math.max(62, Math.min(132, snow ? 66 : 72 + w * 1.6 + (storm ? 18 : 0))));
  const root = 45 + Math.max(0, Math.min(9, Math.round((t - 10) / 10)));   // colder = lower
  const label = storm ? 'Thunderstorm' : snow ? 'Snowfall' : wet ? 'Rain' : cloudy ? 'Overcast' : wx.isDay === false ? 'Clear night' : 'Sunshine';
  return {
    scale, bpm, root, label, storm, snow,
    bright: Math.max(900, Math.min(6000, 900 + (t + 10) * 45)),
    drums: storm ? 3 : w >= 15 ? 2 : w >= 7 ? 1 : 0,
    rain: storm ? 0.16 : wet ? 0.05 + Math.min(0.1, (wx.rain ?? 50) / 800) : snow ? 0.015 : 0,
    key: `${NAMES[root % 12]} ${scale === 'pentatonic' ? 'pentatonic' : scale}`,
  };
}

export function startRadio(wx, { onBar } = {}) {
  const m = moodOf(wx);
  const sc = SCALES[m.scale];
  const pitch = deg => m.root + sc[((deg % 7) + 7) % 7] + 12 * Math.floor(deg / 7);
  let prog = PROGS[m.scale][Math.floor(Math.random() * PROGS[m.scale].length)];
  const rain = m.rain ? rainLayer() : null;
  rain?.set(m.rain);
  let melody = null;

  const ck = clock({
    bpm: m.bpm, stepsPerBeat: 4,
    onStep(step, t) {
      const s16 = step % 16, bar = Math.floor(step / 16);
      const spb = 60 / m.bpm / 4;
      if (s16 === 0) {
        if (bar % 8 === 0 && bar) prog = PROGS[m.scale][Math.floor(Math.random() * PROGS[m.scale].length)];
        const deg = prog[bar % prog.length];
        // pad: the chord, held for the bar
        for (const d of [deg, deg + 2, deg + 4]) tone(midi(pitch(d) + 12), t, spb * 16, { type: 'sawtooth', gain: m.snow ? 0.03 : 0.04, attack: 0.6, release: 0.8, cutoff: m.bright * 0.5, wet: 0.5, detune: (Math.random() - 0.5) * 12 });
        // a little tune for this bar: 4 to 7 notes from the chord and its neighbours
        melody = Array.from({ length: 16 }, (_, i) => (i % 2 === 0 && Math.random() < (m.drums >= 2 ? 0.45 : 0.3) ? deg + [0, 2, 4, 5, 7][Math.floor(Math.random() * 5)] : null));
        onBar?.(bar);
      }
      const deg = prog[bar % prog.length];
      // bass
      if (s16 === 0 || s16 === 8 || (m.drums >= 2 && s16 % 4 === 0)) tone(midi(pitch(deg) - 12), t, spb * 3.5, { type: 'sine', gain: 0.22, release: 0.1, wet: 0 });
      // arpeggio (sunny, windy) or slow bells (snow, night)
      if (m.snow || m.scale === 'lydian') {
        if (s16 % 4 === 0 && Math.random() < 0.6) tone(midi(pitch(deg + [0, 2, 4, 7][s16 / 4]) + 24), t, spb * 6, { type: 'sine', gain: 0.06, attack: 0.005, release: 1.2, wet: 0.7 });
      } else if (m.scale !== 'phrygian' && s16 % 2 === 0) {
        tone(midi(pitch(deg + [0, 2, 4, 2][(s16 / 2) % 4]) + 12), t, spb * 1.6, { type: 'triangle', gain: 0.05, release: 0.12, cutoff: m.bright, wet: 0.25 });
      }
      // melody
      if (melody?.[s16] != null) tone(midi(pitch(melody[s16]) + 24), t, spb * 2.5, { type: 'square', gain: 0.035, attack: 0.02, release: 0.3, cutoff: m.bright * 0.7, wet: 0.4 });
      // drums: calm air = none, breezy = soft, windy/stormy = full
      if (m.drums >= 1 && (s16 === 0 || s16 === 10)) kick(t, m.drums === 1 ? 0.5 : 0.85);
      if (m.drums >= 2 && (s16 === 4 || s16 === 12)) snare(t, m.drums === 3 ? 0.4 : 0.28);
      if (m.drums >= 1 && s16 % 2 === 0) hat(t, s16 % 4 === 2 ? 0.07 : 0.04);
      if (m.storm && s16 === 0 && Math.random() < 0.3) thunder(t + Math.random() * 2, 0.45);
    },
  });
  return { mood: m, stop() { ck.stop(); rain?.stop(); } };
}
